import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Plus, Pencil, Trash2, Loader2, Trophy, TrendingUp, Target, Star, Award,
  Zap, Heart, ThumbsUp, CheckCircle, Flag, Gift, Crown, Medal, Rocket,
  DollarSign, Users, Briefcase, Phone, Mail, Calendar, Clock, FileText,
  ShoppingCart, Home, Car, Plane, Coffee, Music, Camera, Smile, Sun,
  Moon, Cloud, Umbrella, Flame, Leaf, Mountain, Waves, Sparkles, Gem,
  Lightbulb, Bell, Bookmark, Building, Calculator, ChartBar, CircleDollarSign,
  Coins, CreditCard, Diamond, Euro, Fingerprint, Globe, Hammer, Handshake,
  Key, Layers, Link, Lock, Map, MessageCircle, Mic, Package, Palette,
  PenTool, Percent, PieChart, Plug, Power, Presentation, QrCode, Radio,
  Receipt, Repeat, Scale, Search, Send, Settings, Shield, Signal, Smartphone,
  Speaker, Tag, Terminal, Timer, Truck, Tv, Upload, Video, Wallet, Watch,
  Wifi, Wrench, XCircle, Activity, Anchor, Archive, AtSign, Banknote,
  BarChart3, Battery, BellRing, Binary, Bluetooth, Bomb, Bot, Box,
  Brain, Bug, Bus, Cake, CalendarCheck, CameraOff, Cast, ChartLine,
  ChartPie, Check, Cigarette, Clipboard, CloudRain, Code, Compass, Contact,
  Cpu, Database, Dice1, Disc, Download, Droplet, Ear, Edit, Eye, EyeOff,
  FastForward, Feather, File, Filter, Folder, Frown, Gamepad2, Gauge,
  Ghost, Glasses, GraduationCap, Grape, Grid, Hand, HardDrive, Headphones,
  Hexagon, History, Image, Inbox, Info, Keyboard, Laptop,
  LayoutGrid, LifeBuoy, List, ListChecks, Loader, LogIn, LogOut,
  Magnet, Maximize, Menu, MessageSquare, Minimize, Minus, Monitor, MoreHorizontal,
  MousePointer, Move, Navigation, Newspaper, Notebook, PaintBucket, Paperclip,
  Pause, PenSquare, PhoneCall, PhoneOff, Pin, Play, PlusCircle, Pocket,
  Podcast, Printer, Puzzle, RefreshCw, Rewind, RotateCw, Rss, Ruler, Save,
  Scissors, Server, Share, ShoppingBag, Shuffle, Sidebar, SkipBack, SkipForward,
  Sliders, Snowflake, Square, StepForward,
  Stethoscope, Sunrise, Sunset, Table,
  Tablet, Tent, Thermometer, ToggleLeft, ToggleRight, Trash,
  Trees, TrendingDown, Triangle, Type, Undo, Unlink, User,
  UserCheck, UserMinus, UserPlus, UserX, Vibrate, VolumeX, Volume2,
  Wand2, Wind, Wine, ZoomIn, ZoomOut, Siren, Rainbow, Pizza,
  PartyPopper, Milk, IceCreamCone, Croissant, Cherry, Apple, Armchair, Baby,
  Backpack, BadgeCheck, BadgeDollarSign, BadgePercent, Banana, Bath,
  BedDouble, Beer, Bike, Bird, Bone, Book, BookOpen, Boxes, Building2,
  Clapperboard, Construction, Cookie, CupSoda,
  Dna, Dog, Drama, Drumstick, Dumbbell, Factory, Fan, Fish, Flower,
  Footprints, Fuel, Drill, Guitar, Hammer as Hammer2, HardHat, Heart as Heart2,
  Heater, Highlighter, Hospital, Hotel, Hourglass, IceCreamBowl, Joystick, Kanban,
  KeyRound, Lamp, Languages, Lasso, Laugh, Layout, LayoutDashboard, LayoutList,
  Leaf as Leaf2, Library, Ligature, LineChart, ListMinus, ListOrdered, ListPlus,
  ListTree, Locate, LockOpen, Mail as Mail2, MapPin, Martini, Medal as Medal2,
  Megaphone, Meh, MemoryStick, Merge, MessageCirclePlus, Microscope, Microwave,
  Milestone, MilkOff, MonitorSmartphone, MoonStar, MoreVertical, Mountain as Mountain2,
  MousePointer2, MoveHorizontal, MoveVertical, Music2, Music3, Music4, Network,
  Nfc, OctagonX, Option, Orbit, Outdent, PackageCheck, PackageMinus, PackageOpen,
  PackagePlus, PackageSearch, PackageX, PaintbrushVertical, Palmtree, PanelBottom,
  PanelLeft, PanelRight, PanelTop, Parentheses, ParkingCircle, PartyPopper as Party2,
  type LucideIcon
} from "lucide-react";
import { toast } from "sonner";

// Available icons for selection
const AVAILABLE_ICONS: { name: string; icon: LucideIcon }[] = [
  // Achievements & Success
  { name: "trophy", icon: Trophy },
  { name: "award", icon: Award },
  { name: "medal", icon: Medal },
  { name: "crown", icon: Crown },
  { name: "star", icon: Star },
  { name: "sparkles", icon: Sparkles },
  { name: "gem", icon: Gem },
  { name: "diamond", icon: Diamond },
  { name: "graduation-cap", icon: GraduationCap },
  
  // Business & Sales
  { name: "trending-up", icon: TrendingUp },
  { name: "target", icon: Target },
  { name: "chart-bar", icon: ChartBar },
  { name: "chart-line", icon: ChartLine },
  { name: "chart-pie", icon: ChartPie },
  { name: "bar-chart", icon: BarChart3 },
  { name: "pie-chart", icon: PieChart },
  { name: "briefcase", icon: Briefcase },
  { name: "building", icon: Building },
  { name: "handshake", icon: Handshake },
  { name: "presentation", icon: Presentation },
  { name: "clipboard", icon: Clipboard },
  
  // Money & Finance
  { name: "dollar-sign", icon: DollarSign },
  { name: "euro", icon: Euro },
  { name: "coins", icon: Coins },
  { name: "banknote", icon: Banknote },
  { name: "wallet", icon: Wallet },
  { name: "credit-card", icon: CreditCard },
  { name: "circle-dollar", icon: CircleDollarSign },
  { name: "receipt", icon: Receipt },
  { name: "percent", icon: Percent },
  { name: "calculator", icon: Calculator },
  { name: "scale", icon: Scale },
  
  // Actions & Progress
  { name: "check-circle", icon: CheckCircle },
  { name: "check", icon: Check },
  { name: "thumbs-up", icon: ThumbsUp },
  { name: "zap", icon: Zap },
  { name: "rocket", icon: Rocket },
  { name: "flag", icon: Flag },
  { name: "lightbulb", icon: Lightbulb },
  { name: "gauge", icon: Gauge },
  { name: "activity", icon: Activity },
  { name: "timer", icon: Timer },
  { name: "repeat", icon: Repeat },
  
  // People & Communication
  { name: "users", icon: Users },
  { name: "contact", icon: Contact },
  { name: "phone", icon: Phone },
  { name: "mail", icon: Mail },
  { name: "message-circle", icon: MessageCircle },
  { name: "send", icon: Send },
  { name: "bell", icon: Bell },
  { name: "bell-ring", icon: BellRing },
  { name: "mic", icon: Mic },
  { name: "headphones", icon: Headphones },
  
  // Objects & Items
  { name: "gift", icon: Gift },
  { name: "heart", icon: Heart },
  { name: "shopping-cart", icon: ShoppingCart },
  { name: "package", icon: Package },
  { name: "box", icon: Box },
  { name: "tag", icon: Tag },
  { name: "bookmark", icon: Bookmark },
  { name: "file-text", icon: FileText },
  { name: "file", icon: File },
  { name: "folder", icon: Folder },
  { name: "archive", icon: Archive },
  
  // Time & Calendar
  { name: "calendar", icon: Calendar },
  { name: "calendar-check", icon: CalendarCheck },
  { name: "clock", icon: Clock },
  { name: "watch", icon: Watch },
  
  // Places & Transport
  { name: "home", icon: Home },
  { name: "car", icon: Car },
  { name: "truck", icon: Truck },
  { name: "plane", icon: Plane },
  { name: "bus", icon: Bus },
  { name: "map", icon: Map },
  { name: "compass", icon: Compass },
  { name: "globe", icon: Globe },
  
  // Tools & Tech
  { name: "wrench", icon: Wrench },
  { name: "hammer", icon: Hammer },
  { name: "settings", icon: Settings },
  { name: "key", icon: Key },
  { name: "lock", icon: Lock },
  { name: "shield", icon: Shield },
  { name: "plug", icon: Plug },
  { name: "power", icon: Power },
  { name: "cpu", icon: Cpu },
  { name: "database", icon: Database },
  { name: "hard-drive", icon: HardDrive },
  { name: "smartphone", icon: Smartphone },
  { name: "wifi", icon: Wifi },
  { name: "signal", icon: Signal },
  { name: "bluetooth", icon: Bluetooth },
  
  // Media & Creative
  { name: "camera", icon: Camera },
  { name: "video", icon: Video },
  { name: "music", icon: Music },
  { name: "palette", icon: Palette },
  { name: "pen-tool", icon: PenTool },
  { name: "edit", icon: Edit },
  { name: "tv", icon: Tv },
  { name: "radio", icon: Radio },
  { name: "speaker", icon: Speaker },
  { name: "gamepad", icon: Gamepad2 },
  
  // Nature & Weather
  { name: "sun", icon: Sun },
  { name: "moon", icon: Moon },
  { name: "cloud", icon: Cloud },
  { name: "cloud-rain", icon: CloudRain },
  { name: "umbrella", icon: Umbrella },
  { name: "flame", icon: Flame },
  { name: "droplet", icon: Droplet },
  { name: "leaf", icon: Leaf },
  { name: "mountain", icon: Mountain },
  { name: "waves", icon: Waves },
  { name: "feather", icon: Feather },
  { name: "anchor", icon: Anchor },
  
  // Fun & Misc
  { name: "smile", icon: Smile },
  { name: "frown", icon: Frown },
  { name: "coffee", icon: Coffee },
  { name: "cake", icon: Cake },
  { name: "grape", icon: Grape },
  { name: "glasses", icon: Glasses },
  { name: "ghost", icon: Ghost },
  { name: "brain", icon: Brain },
  { name: "bot", icon: Bot },
  { name: "bug", icon: Bug },
  { name: "bomb", icon: Bomb },
  { name: "dice", icon: Dice1 },
  { name: "hand", icon: Hand },
  { name: "eye", icon: Eye },
  { name: "ear", icon: Ear },
  { name: "fingerprint", icon: Fingerprint },
  
  // Extended Icons
  { name: "hexagon", icon: Hexagon },
  { name: "history", icon: History },
  { name: "image", icon: Image },
  { name: "inbox", icon: Inbox },
  { name: "info", icon: Info },
  { name: "keyboard", icon: Keyboard },
  { name: "laptop", icon: Laptop },
  { name: "layout-grid", icon: LayoutGrid },
  { name: "life-buoy", icon: LifeBuoy },
  { name: "list", icon: List },
  { name: "list-checks", icon: ListChecks },
  { name: "loader", icon: Loader },
  { name: "log-in", icon: LogIn },
  { name: "log-out", icon: LogOut },
  { name: "magnet", icon: Magnet },
  { name: "maximize", icon: Maximize },
  { name: "menu", icon: Menu },
  { name: "message-square", icon: MessageSquare },
  { name: "minimize", icon: Minimize },
  { name: "minus", icon: Minus },
  { name: "monitor", icon: Monitor },
  { name: "more-horizontal", icon: MoreHorizontal },
  { name: "mouse-pointer", icon: MousePointer },
  { name: "move", icon: Move },
  { name: "navigation", icon: Navigation },
  { name: "newspaper", icon: Newspaper },
  { name: "notebook", icon: Notebook },
  { name: "paint-bucket", icon: PaintBucket },
  { name: "paperclip", icon: Paperclip },
  { name: "pause", icon: Pause },
  { name: "pen-square", icon: PenSquare },
  { name: "phone-call", icon: PhoneCall },
  { name: "phone-off", icon: PhoneOff },
  { name: "pin", icon: Pin },
  { name: "play", icon: Play },
  { name: "plus-circle", icon: PlusCircle },
  { name: "pocket", icon: Pocket },
  { name: "podcast", icon: Podcast },
  { name: "printer", icon: Printer },
  { name: "puzzle", icon: Puzzle },
  { name: "refresh", icon: RefreshCw },
  { name: "rewind", icon: Rewind },
  { name: "rotate", icon: RotateCw },
  { name: "rss", icon: Rss },
  { name: "ruler", icon: Ruler },
  { name: "save", icon: Save },
  { name: "scissors", icon: Scissors },
  { name: "server", icon: Server },
  { name: "share", icon: Share },
  { name: "shopping-bag", icon: ShoppingBag },
  { name: "shuffle", icon: Shuffle },
  { name: "sidebar", icon: Sidebar },
  { name: "skip-back", icon: SkipBack },
  { name: "skip-forward", icon: SkipForward },
  { name: "sliders", icon: Sliders },
  { name: "snowflake", icon: Snowflake },
  { name: "square", icon: Square },
  { name: "step-forward", icon: StepForward },
  { name: "stethoscope", icon: Stethoscope },
  { name: "sunrise", icon: Sunrise },
  { name: "sunset", icon: Sunset },
  { name: "table", icon: Table },
  { name: "tablet", icon: Tablet },
  { name: "tent", icon: Tent },
  { name: "thermometer", icon: Thermometer },
  { name: "toggle-left", icon: ToggleLeft },
  { name: "toggle-right", icon: ToggleRight },
  { name: "trash", icon: Trash },
  { name: "trees", icon: Trees },
  { name: "trending-down", icon: TrendingDown },
  { name: "triangle", icon: Triangle },
  { name: "type", icon: Type },
  { name: "undo", icon: Undo },
  { name: "unlink", icon: Unlink },
  { name: "user", icon: User },
  { name: "user-check", icon: UserCheck },
  { name: "user-minus", icon: UserMinus },
  { name: "user-plus", icon: UserPlus },
  { name: "user-x", icon: UserX },
  { name: "vibrate", icon: Vibrate },
  { name: "volume-x", icon: VolumeX },
  { name: "volume", icon: Volume2 },
  { name: "wand", icon: Wand2 },
  { name: "wind", icon: Wind },
  { name: "wine", icon: Wine },
  { name: "zoom-in", icon: ZoomIn },
  { name: "zoom-out", icon: ZoomOut },
  { name: "siren", icon: Siren },
  { name: "rainbow", icon: Rainbow },
  { name: "pizza", icon: Pizza },
  { name: "party", icon: PartyPopper },
  { name: "milk", icon: Milk },
  { name: "ice-cream", icon: IceCreamCone },
  { name: "croissant", icon: Croissant },
  { name: "cherry", icon: Cherry },
  { name: "apple", icon: Apple },
  { name: "armchair", icon: Armchair },
  { name: "baby", icon: Baby },
  { name: "backpack", icon: Backpack },
  { name: "badge-check", icon: BadgeCheck },
  { name: "badge-dollar", icon: BadgeDollarSign },
  { name: "badge-percent", icon: BadgePercent },
  { name: "banana", icon: Banana },
  { name: "bath", icon: Bath },
  { name: "bed", icon: BedDouble },
  { name: "beer", icon: Beer },
  { name: "bike", icon: Bike },
  { name: "bird", icon: Bird },
  { name: "bone", icon: Bone },
  { name: "book", icon: Book },
  { name: "book-open", icon: BookOpen },
  { name: "boxes", icon: Boxes },
  { name: "building-2", icon: Building2 },
  { name: "clapperboard", icon: Clapperboard },
  { name: "construction", icon: Construction },
  { name: "cookie", icon: Cookie },
  { name: "cup-soda", icon: CupSoda },
  { name: "dna", icon: Dna },
  { name: "dog", icon: Dog },
  { name: "drama", icon: Drama },
  { name: "drumstick", icon: Drumstick },
  { name: "dumbbell", icon: Dumbbell },
  { name: "factory", icon: Factory },
  { name: "fan", icon: Fan },
  { name: "fish", icon: Fish },
  { name: "flower", icon: Flower },
  { name: "footprints", icon: Footprints },
  { name: "fuel", icon: Fuel },
  { name: "drill", icon: Drill },
  { name: "guitar", icon: Guitar },
  { name: "hard-hat", icon: HardHat },
  { name: "heater", icon: Heater },
  { name: "highlighter", icon: Highlighter },
  { name: "hospital", icon: Hospital },
  { name: "hotel", icon: Hotel },
  { name: "hourglass", icon: Hourglass },
  { name: "ice-cream-bowl", icon: IceCreamBowl },
  { name: "joystick", icon: Joystick },
  { name: "kanban", icon: Kanban },
  { name: "key-round", icon: KeyRound },
  { name: "lamp", icon: Lamp },
  { name: "languages", icon: Languages },
  { name: "lasso", icon: Lasso },
  { name: "laugh", icon: Laugh },
  { name: "layout", icon: Layout },
  { name: "layout-dashboard", icon: LayoutDashboard },
  { name: "layout-list", icon: LayoutList },
  { name: "library", icon: Library },
  { name: "line-chart", icon: LineChart },
  { name: "list-minus", icon: ListMinus },
  { name: "list-ordered", icon: ListOrdered },
  { name: "list-plus", icon: ListPlus },
  { name: "list-tree", icon: ListTree },
  { name: "locate", icon: Locate },
  { name: "lock-open", icon: LockOpen },
  { name: "map-pin", icon: MapPin },
  { name: "martini", icon: Martini },
  { name: "megaphone", icon: Megaphone },
  { name: "meh", icon: Meh },
  { name: "memory-stick", icon: MemoryStick },
  { name: "merge", icon: Merge },
  { name: "message-circle-plus", icon: MessageCirclePlus },
  { name: "microscope", icon: Microscope },
  { name: "microwave", icon: Microwave },
  { name: "milestone", icon: Milestone },
  { name: "monitor-smartphone", icon: MonitorSmartphone },
  { name: "moon-star", icon: MoonStar },
  { name: "more-vertical", icon: MoreVertical },
  { name: "mouse-pointer-2", icon: MousePointer2 },
  { name: "move-horizontal", icon: MoveHorizontal },
  { name: "move-vertical", icon: MoveVertical },
  { name: "music-2", icon: Music2 },
  { name: "music-3", icon: Music3 },
  { name: "music-4", icon: Music4 },
  { name: "network", icon: Network },
  { name: "nfc", icon: Nfc },
  { name: "option", icon: Option },
  { name: "orbit", icon: Orbit },
  { name: "package-check", icon: PackageCheck },
  { name: "package-minus", icon: PackageMinus },
  { name: "package-open", icon: PackageOpen },
  { name: "package-plus", icon: PackagePlus },
  { name: "package-search", icon: PackageSearch },
  { name: "paintbrush", icon: PaintbrushVertical },
  { name: "palmtree", icon: Palmtree },
  { name: "panel-bottom", icon: PanelBottom },
  { name: "panel-left", icon: PanelLeft },
  { name: "panel-right", icon: PanelRight },
  { name: "panel-top", icon: PanelTop },
  { name: "parking", icon: ParkingCircle },
];

// Helper to get icon component by name
const getIconByName = (name: string): LucideIcon => {
  const found = AVAILABLE_ICONS.find(i => i.name === name);
  return found?.icon || Trophy;
};

interface PerformanceMetric {
  id: string;
  name: string;
  description: string | null;
  points_value: number;
  is_active: boolean;
  icon: string | null;
}

interface TeamPerformanceMetricsProps {
  teamId: string;
}

export function TeamPerformanceMetrics({ teamId }: TeamPerformanceMetricsProps) {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<PerformanceMetric | null>(null);
  const [saving, setSaving] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    points_value: 1,
    icon: "trophy"
  });

  useEffect(() => {
    fetchMetrics();
  }, [teamId]);

  const fetchMetrics = async () => {
    try {
      const { data, error } = await supabase
        .from('team_performance_metrics')
        .select('*')
        .eq('team_id', teamId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setMetrics(data || []);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Fehler beim Laden der Metriken');
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditingMetric(null);
    setFormData({ name: "", description: "", points_value: 1, icon: "trophy" });
    setDialogOpen(true);
  };

  const openEditDialog = (metric: PerformanceMetric) => {
    setEditingMetric(metric);
    setFormData({
      name: metric.name,
      description: metric.description || "",
      points_value: metric.points_value,
      icon: metric.icon || "trophy"
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !user) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    setSaving(true);
    try {
      if (editingMetric) {
        // Update existing metric
        const { error } = await supabase
          .from('team_performance_metrics')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            points_value: formData.points_value,
            icon: formData.icon
          })
          .eq('id', editingMetric.id);

        if (error) throw error;
        toast.success('Metrik aktualisiert');
      } else {
        // Create new metric
        const { error } = await supabase
          .from('team_performance_metrics')
          .insert({
            team_id: teamId,
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            points_value: formData.points_value,
            icon: formData.icon,
            created_by: user.id
          });

        if (error) throw error;
        toast.success('Metrik erstellt');
      }

      setDialogOpen(false);
      fetchMetrics();
    } catch (error) {
      console.error('Error saving metric:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (metricId: string) => {
    if (!confirm('Möchten Sie diese Metrik wirklich löschen? Alle zugehörigen Performance-Einträge werden ebenfalls gelöscht.')) return;

    try {
      // Delete performance entries first
      await supabase
        .from('team_member_performance')
        .delete()
        .eq('metric_id', metricId);

      // Then delete the metric
      const { error } = await supabase
        .from('team_performance_metrics')
        .delete()
        .eq('id', metricId);

      if (error) throw error;
      toast.success('Metrik gelöscht');
      fetchMetrics();
    } catch (error) {
      console.error('Error deleting metric:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (metric: PerformanceMetric) => {
    try {
      const { error } = await supabase
        .from('team_performance_metrics')
        .update({ is_active: !metric.is_active })
        .eq('id', metric.id);

      if (error) throw error;
      toast.success(metric.is_active ? 'Metrik deaktiviert' : 'Metrik aktiviert');
      fetchMetrics();
    } catch (error) {
      console.error('Error toggling metric:', error);
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const SelectedIcon = getIconByName(formData.icon);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Leistungsmetriken</CardTitle>
          <CardDescription>
            Definieren Sie Punkte-Systeme zur Leistungserfassung
          </CardDescription>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Neue Metrik
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingMetric ? 'Metrik bearbeiten' : 'Neue Metrik erstellen'}
              </DialogTitle>
              <DialogDescription>
                Erstellen Sie eine Metrik zur Leistungserfassung.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="z.B. Vertragsabschluss"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung (optional)</Label>
                <Input
                  id="description"
                  placeholder="Beschreibung der Metrik..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol</Label>
                <Popover open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-3"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <SelectedIcon className="h-4 w-4 text-primary" />
                      </div>
                      <span>Symbol auswählen</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-[400px] p-0 bg-popover" 
                    align="start" 
                    side="bottom"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    <div 
                      className="max-h-[400px] overflow-y-scroll overscroll-contain p-3"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      <div className="grid grid-cols-10 gap-1">
                        {AVAILABLE_ICONS.map(({ name, icon: IconComponent }) => (
                          <Button
                            key={name}
                            variant={formData.icon === name ? "default" : "ghost"}
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setFormData({ ...formData, icon: name });
                              setIconPickerOpen(false);
                            }}
                          >
                            <IconComponent className="h-4 w-4" />
                          </Button>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="points">Punkte pro Eintrag</Label>
                <Input
                  id="points"
                  type="number"
                  min={0}
                  max={1000}
                  value={formData.points_value}
                  onChange={(e) => setFormData({ ...formData, points_value: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingMetric ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {metrics.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Keine Metriken vorhanden</h3>
            <p className="text-muted-foreground mb-4">
              Erstellen Sie Ihre erste Performance-Metrik
            </p>
            <Button onClick={openNewDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Erste Metrik erstellen
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {metrics.map((metric) => {
              const MetricIcon = getIconByName(metric.icon || "trophy");
              return (
                <div
                  key={metric.id}
                  className={`flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors ${
                    !metric.is_active ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MetricIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">{metric.name}</h4>
                      {metric.description && (
                        <p className="text-sm text-muted-foreground">
                          {metric.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-2xl font-bold text-primary">{metric.points_value}</span>
                      <p className="text-xs text-muted-foreground">Punkte</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleActive(metric)}
                      >
                        {metric.is_active ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(metric)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDelete(metric.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
